import { ReleaseChannel, ReleaseChannelLabels } from '@navio/nsdk';
import { type InputData, URLKeys } from 'noddi-async';
import { filterUnitFor } from 'noddi-ui-web';
import {
  CUSTOMER_GROUP_TYPE_OPTIONS,
  CUSTOMER_SEGMENT_OPTIONS,
  entriesToOptions,
  RELEASE_CHANNEL_ICON_MAP,
  USER_GROUP_TYPE_ICON_MAP
} from 'noddi-util';
import { useBrandOptions } from '../../hooks/filters/Brand/useBrandFilter';
import { useCouponGroupOptions } from '../../hooks/filters/CouponGroups/useCouponGroupsFilter';
import { useMembershipProgramOptions } from '../../hooks/filters/MembershipPrograms/useMembershipProgramsFilter';
import { useStorageFacilityOptions } from '../../hooks/filters/StorageFacility/useStorageFacilitiesFilter';
import { PHONE_NUMBER_TYPE_FILTER_OPTIONS } from '../../hooks/filters/UserGroup/usePhoneNumberTypeFilter';
import {
  DATA_QUALITY_ISSUE_TYPES_FILTER,
  HAS_DATA_QUALITY_ISSUES_FILTER_TEXT
} from '../../hooks/filters/UserGroup/useUserGroupDataQualityFilters';

const RELEASE_CHANNEL_FILTER_OPTIONS = entriesToOptions(ReleaseChannel, ReleaseChannelLabels).map((option) => ({
  ...option,
  icon: RELEASE_CHANNEL_ICON_MAP[option.value]
}));

const CUSTOMER_TYPE_FILTER_OPTIONS = CUSTOMER_GROUP_TYPE_OPTIONS.map((option) => ({
  ...option,
  icon: USER_GROUP_TYPE_ICON_MAP[option.value]
}));

type PageKeys = 'pageIndex' | 'pageSize' | 'order' | 'search';
type UserImpersonationFilterInput = Omit<InputData<typeof URLKeys.getPaginatedAccountsIgnoringImpersonation>, PageKeys>;
export type OrganizationImpersonationFilterInput = Omit<
  InputData<typeof URLKeys.getPaginatedUserGroupsIgnoringImpersonation>,
  PageKeys
>;

export const userImpersonationFilterUnit = filterUnitFor<UserImpersonationFilterInput>();
export const organizationImpersonationFilterUnit = filterUnitFor<OrganizationImpersonationFilterInput>();

/**
 * Data quality writes the user group query: the Organizations tab filters on it, and the Users tab resolves it to
 * the user group ids the user list takes (`useImpersonationUserGroupScope`).
 */
export const impersonationDataQualityFilters = {
  hasIssues: organizationImpersonationFilterUnit.boolean('hasDataQualityIssues', HAS_DATA_QUALITY_ISSUES_FILTER_TEXT),
  issueTypes: organizationImpersonationFilterUnit.multiSelect('dataQualityIssueTypes', {
    ...DATA_QUALITY_ISSUE_TYPES_FILTER,
    exclude: true
  })
};

const TEXT = {
  membershipPrograms: {
    title: 'Membership programs',
    description: 'Only show members of the selected membership programs',
    defaultVisibleOptions: 3
  },
  segments: {
    title: 'Customer segment',
    description: 'Filter by lifecycle or loyalty segment on the customer',
    options: CUSTOMER_SEGMENT_OPTIONS
  },
  hasTireInTireHotel: {
    title: 'Has tire in tire hotel',
    description: 'Only show customers with tires currently stored in a tire hotel'
  },
  numCars: {
    title: 'Number of cars',
    description: 'Filter customers by how many cars they currently own',
    minLabel: 'Min',
    maxLabel: 'Max'
  }
};

/**
 * The impersonation dialog's customer filters. Never in the page URL: the dialog keeps them in a local scope
 * (`NavioLocalQueryParamProvider`). Both tabs read the same keys, so switching tabs keeps the selection. Brand,
 * organization, department, role and data quality scope the users through other queries: the dialog owns them.
 */
export const useImpersonationUserFilters = () => {
  const brands = useBrandOptions();
  const membershipPrograms = useMembershipProgramOptions();
  const couponGroups = useCouponGroupOptions();
  const storageFacilities = useStorageFacilityOptions({ heldBy: 'storageFacilityIds' });

  const userFilters = [
    userImpersonationFilterUnit.multiSelect('brandRelationshipIds', {
      title: 'Brand relationship',
      description: 'Filter by the customer initial brand (first booking brand)',
      source: brands,
      exclude: true
    }),
    // The user list takes no excluded membership programs or coupon groups, so these offer no Exclude here.
    userImpersonationFilterUnit.multiSelect('membershipProgramIds', {
      ...TEXT.membershipPrograms,
      source: membershipPrograms
    }),
    userImpersonationFilterUnit.multiSelect('couponGroupIds', {
      title: 'Coupon groups',
      description: 'Only show users with an unused coupon in the selected referral coupon group',
      defaultVisibleOptions: 3,
      source: couponGroups
    }),
    userImpersonationFilterUnit.multiSelect('storageFacilityIds', {
      title: 'Tire hotel at storage facilities',
      description: 'Only show customers with tires currently stored at the selected facilities',
      defaultVisibleOptions: 3,
      source: storageFacilities,
      exclude: true
    }),
    userImpersonationFilterUnit.multiSelect('userGroupTypes', {
      title: 'Customer type',
      description: 'Filter by type of the customer (personal, organization, group)',
      options: CUSTOMER_TYPE_FILTER_OPTIONS,
      exclude: true
    }),
    userImpersonationFilterUnit.multiSelect('segments', { ...TEXT.segments, exclude: true }),
    userImpersonationFilterUnit.multiSelect('phoneNumberTypes', {
      title: 'Phone number type',
      description: 'Filter by phone number type (e.g. mobile, landline).',
      options: PHONE_NUMBER_TYPE_FILTER_OPTIONS,
      exclude: true
    }),
    userImpersonationFilterUnit.boolean('invalidEmail', {
      title: 'Missing or invalid email',
      description: 'Filter customers with no usable email address.'
    }),
    userImpersonationFilterUnit.boolean('invalidPhone', {
      title: 'Invalid phone',
      description: 'Filter on whether the phone number is missing, not mobile, or blocked from SMS.'
    }),
    userImpersonationFilterUnit.multiSelect('releaseChannels', {
      title: 'Release channel',
      description: 'Filter by feature release channel on the customer',
      options: RELEASE_CHANNEL_FILTER_OPTIONS,
      exclude: true
    }),
    userImpersonationFilterUnit.boolean('hasTireInTireHotel', TEXT.hasTireInTireHotel),
    userImpersonationFilterUnit.numberRange('numCars', { ...TEXT.numCars, exclude: true }),
    userImpersonationFilterUnit.dateRange('personalInfoConfirmedAt', {
      title: 'Personal info confirmed at',
      description: 'Filter by when personal information was last confirmed',
      exclude: true
    })
  ];

  const organizationFilters = [
    organizationImpersonationFilterUnit.multiSelect('membershipProgramIds', {
      ...TEXT.membershipPrograms,
      source: membershipPrograms,
      exclude: true
    }),
    organizationImpersonationFilterUnit.multiSelect('segments', { ...TEXT.segments, exclude: true }),
    organizationImpersonationFilterUnit.boolean('hasTireInTireHotel', TEXT.hasTireInTireHotel),
    organizationImpersonationFilterUnit.numberRange('numCars', { ...TEXT.numCars, exclude: true }),
    impersonationDataQualityFilters.hasIssues,
    impersonationDataQualityFilters.issueTypes
  ];

  return { userFilters, organizationFilters };
};
